/**
 * @fileoverview App shell for the redesigned instructor portal. Fixed 256px
 * left sidebar on md+, collapsing to a slim top bar + slide-in drawer on
 * mobile. Wraps the instructor routes as a layout route (renders <Outlet/>).
 */

import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SidebarInner } from "@/components/studio/StudioSidebar";
import { Icon } from "@/components/studio/primitives";

const SIDEBAR_W = 256;

export default function StudioLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Reset scroll on navigation (parity with the prototype's goto()).
  useEffect(() => {
    setDrawerOpen(false);
    document.getElementById("studio-main")?.scrollTo?.({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  // Multi-org (Spec 12): the shell is vanilla Hans — an instructor may belong
  // to several orgs, so there is no single account-wide co-brand. Program
  // co-branding lives per-lesson (email + program pages), not on the shell.

  return (
    <div className="studio min-h-screen bg-[var(--studio-base)] text-[var(--text-primary)]">
      {/* Desktop rail */}
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden border-r border-[var(--studio-divider)] bg-[var(--studio-sidebar)] md:block"
        style={{ width: SIDEBAR_W }}
      >
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-[var(--studio-divider)] bg-[var(--studio-sidebar)] px-4 md:hidden">
        <button
          type="button"
          aria-label="Open menu"
          data-testid="studio-menu"
          onClick={() => setDrawerOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-[12px] text-[var(--text-secondary)] hover:bg-[var(--studio-raised)]"
        >
          <Icon name="menu" size={22} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo192.png" alt="Hans" className="h-7 w-7 rounded-[8px]" />
          <span className="font-sans text-base font-bold text-[var(--text-primary)]">Hans</span>
        </div>
        <button
          type="button"
          aria-label="Record lesson"
          onClick={() => navigate("/hans")}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--accent-yellow)] shadow-[0_4px_16px_var(--accent-yellow-glow)]"
        >
          <Icon name="mic" size={18} strokeWidth={2.2} className="text-[var(--text-inverse)]" />
        </button>
      </header>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="left"
          className="studio w-[256px] border-[var(--studio-divider)] bg-[var(--studio-sidebar)] p-0"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarInner onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Content */}
      <main id="studio-main" className="min-h-screen pt-14 md:ml-64 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
